import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    name: 'Root',
    meta: { hidden: true }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: { hidden: true, noTagsView: true }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: { hidden: true, title: '登录', noTagsView: true }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: { hidden: true, title: '404', noTagsView: true }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    name: 'Dashboard',
    meta: { title: '系统概览', icon: 'vi-ep:data-analysis', alwaysShow: false },
    children: [
      {
        path: '',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'DashboardIndex',
        meta: { title: '系统概览', affix: true, noCache: true }
      }
    ]
  },
  {
    path: '/device',
    component: Layout,
    name: 'Device',
    meta: { title: '设备管理', icon: 'vi-ep:monitor', alwaysShow: true },
    children: [
      {
        path: 'serial-numbers',
        component: () => import('@/views/Device/SerialNumbers.vue'),
        name: 'SerialNumbers',
        meta: { title: '序列号管理' }
      },
      {
        path: 'device-types',
        component: () => import('@/views/Device/DeviceTypes.vue'),
        name: 'DeviceTypes',
        meta: { title: '设备型号' }
      }
    ]
  },
  {
    path: '/membership',
    component: Layout,
    name: 'Membership',
    meta: { title: '会员管理', icon: 'vi-ep:ticket', alwaysShow: true },
    children: [
      {
        path: 'plans',
        component: () => import('@/views/Membership/Plans.vue'),
        name: 'MembershipPlans',
        meta: { title: '会员套餐' }
      },
      {
        path: 'devices',
        component: () => import('@/views/Membership/DeviceMember.vue'),
        name: 'DeviceMember',
        meta: { title: '设备会员' }
      }
    ]
  },
  {
    path: '/voice',
    component: Layout,
    name: 'Voice',
    meta: { title: '语音配置', icon: 'vi-ep:microphone', alwaysShow: false },
    children: [
      {
        path: '',
        component: () => import('@/views/Voice/VoiceConfig.vue'),
        name: 'VoiceConfig',
        meta: { title: '语音服务配置', icon: 'vi-ep:microphone' }
      },
      {
        path: 'file-asr',
        component: () => import('@/views/Voice/FileAsrConfig.vue'),
        name: 'FileAsrConfig',
        meta: { title: '录音文件识别配置', icon: 'vi-ep:headset' }
      },
      {
        path: 'subtitle',
        component: () => import('@/views/Voice/SubtitleConfig.vue'),
        name: 'SubtitleConfig',
        meta: { title: '实时字幕配置', icon: 'vi-ep:chat-line-square' }
      }
    ]
  },
  {
    path: '/allowlist',
    component: Layout,
    name: 'Allowlist',
    meta: { title: '白名单', icon: 'vi-ep:lock', alwaysShow: false },
    children: [
      {
        path: '',
        component: () => import('@/views/Allowlist/Allowlist.vue'),
        name: 'AllowlistIndex',
        meta: { title: '白名单管理', icon: 'vi-ep:lock' }
      }
    ]
  },
  {
    path: '/ota',
    component: Layout,
    name: 'OTA',
    meta: { title: 'OTA 固件', icon: 'vi-ep:upload', alwaysShow: false },
    children: [
      {
        path: '',
        component: () => import('@/views/OTA/Versions.vue'),
        name: 'OTAVersions',
        meta: { title: 'OTA 版本管理', icon: 'vi-ep:upload' }
      }
    ]
  },
  {
    path: '/download-config',
    component: Layout,
    name: 'DownloadConfig',
    meta: { title: '下载配置', icon: 'vi-ep:download', alwaysShow: false },
    children: [
      {
        path: '',
        component: () => import('@/views/DownloadConfig/DownloadConfig.vue'),
        name: 'DownloadConfigIndex',
        meta: { title: '下载分流配置', icon: 'vi-ep:download' }
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: { title: '错误页面', icon: 'vi-ci:error', alwaysShow: true, hidden: true },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: { title: '404' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
